import { ArrowUp, CalendarCheck, Lock, Search, Star } from "lucide-react";
import type { CSSProperties } from "react";

import { CcPhotoScene, CcToolLogo } from "@/components/services/shared/CcPhotoScene";

import type { SeoValueDemoCopy } from "./seo-value-demo-copy";

/**
 * The three "Värdet av SEO" tabs as real photos with cards for the made-up
 * plumbing firm laid over them: they find you on Google, they visit your
 * page, they get in touch. Copy comes from `seo-value-demo.json`.
 */

const delay = (d: number) => ({ "--d": d }) as CSSProperties;

function Stars() {
  return (
    <span className="cc-wv-review__stars">
      {[0, 1, 2, 3, 4].map((star) => (
        <Star key={star} size={11} fill="currentColor" strokeWidth={0} />
      ))}
    </span>
  );
}

// ---------------------------------------------------------------- 1. they find you

export function SeoFoundPhotoView({ copy }: { copy: SeoValueDemoCopy["found"] }) {
  const ours = copy.listings.find((listing) => listing.ours) ?? copy.listings[0];
  const others = copy.listings.filter((listing) => listing !== ours).slice(0, 1);
  return (
    <CcPhotoScene photo={copy.photo} variant="seo-found">
      <div className="cc-wv-card cc-sv-serp" style={delay(0)}>
        <p className="cc-sv-serp__bar">
          <CcToolLogo name="google" size={18} />
          <span>
            <Search size={13} strokeWidth={2.2} />
            {copy.query}
          </span>
        </p>
        <p className="cc-sv-serp__label">{copy.placesTitle}</p>
        <ul>
          {[ours, ...others].map((listing) => (
            <li key={listing.name} data-ours={listing.ours}>
              <b>{listing.name}</b>
              <span>
                {listing.rating} <Stars /> ({listing.reviews})
              </span>
              <small>{listing.meta}</small>
            </li>
          ))}
        </ul>
        <div className="cc-sv-serp__result">
          <small>{copy.result.url}</small>
          <b>{copy.result.title}</b>
        </div>
      </div>

      <div className="cc-wv-card cc-sv-ranks" style={delay(1)}>
        <p className="cc-wv-card__head">{copy.ranksTitle}</p>
        <ul>
          {copy.ranks.slice(0, 3).map((rank) => (
            <li key={rank.keyword}>
              <span>{rank.keyword}</span>
              <b>{rank.position}</b>
              <em>
                <ArrowUp size={11} strokeWidth={2.6} />
                {rank.change}
              </em>
            </li>
          ))}
        </ul>
      </div>
    </CcPhotoScene>
  );
}

// ---------------------------------------------------------------- 2. they visit you

export function SeoVisitPhotoView({ copy }: { copy: SeoValueDemoCopy["visit"] }) {
  const max = Math.max(...copy.visitsSeries);
  return (
    <CcPhotoScene photo={copy.photo} variant="seo-visit">
      <div className="cc-wv-card cc-wv-site cc-sv-site" style={delay(0)}>
        <div className="cc-wv-site__bar">
          <span>
            <i />
            <i />
            <i />
          </span>
          <em>
            <Lock size={10} strokeWidth={2.4} />
            {copy.url}
          </em>
        </div>
        <div className="cc-wv-site__body">
          <div className="cc-wv-site__nav">
            <b>{copy.brand}</b>
            <span>
              {copy.nav.map((item) => (
                <em key={item}>{item}</em>
              ))}
            </span>
            <i>{copy.navCta}</i>
          </div>
          <strong>{copy.heading}</strong>
          <p>{copy.text}</p>
          <i className="cc-wv-site__cta">{copy.cta}</i>
          <ul>
            {copy.trust.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      </div>

      <div className="cc-wv-card cc-sv-visits" style={delay(1)}>
        <p className="cc-wv-card__head">
          <CcToolLogo name="google-analytics" />
          {copy.visitsTitle}
          <em>{copy.visitsPeriod}</em>
        </p>
        <p className="cc-sv-visits__value">
          <b>{copy.visitsValue}</b>
          <span>{copy.visitsChange}</span>
        </p>
        <ul className="cc-sv-visits__bars">
          {copy.visitsSeries.map((value, index) => (
            <li
              key={index}
              style={{ "--h": `${(value / max) * 100}%`, "--i": index } as CSSProperties}
            />
          ))}
        </ul>
      </div>
    </CcPhotoScene>
  );
}

// ---------------------------------------------------------------- 3. they get in touch

export function SeoContactPhotoView({ copy }: { copy: SeoValueDemoCopy["contact"] }) {
  const enquiry = copy.rows[0];
  return (
    <CcPhotoScene photo={copy.photo} variant="seo-contact">
      <div className="cc-wv-card cc-wv-mail" style={delay(0)}>
        <p className="cc-wv-card__head">
          <CcToolLogo name="gmail" />
          {copy.mailApp}
          <em>{copy.mailTime}</em>
        </p>
        <b>{copy.mailTitle}</b>
        <small>{enquiry.name}</small>
        <p>{enquiry.subject}</p>
        <span className="cc-wv-mail__source">
          <CcToolLogo name="google" size={12} />
          {copy.source}
        </span>
      </div>

      <div className="cc-wv-card cc-wv-booking" style={delay(1)}>
        <CcToolLogo name="google-calendar" size={26} />
        <span>
          <small>{copy.booking.app}</small>
          <b>{copy.booking.title}</b>
          <em>{copy.booking.when}</em>
        </span>
        <span className="cc-wv-booking__note">
          <CalendarCheck size={13} strokeWidth={2.2} />
          {copy.booking.note}
        </span>
      </div>
    </CcPhotoScene>
  );
}
